// src/pages/Connector/Instagram/CommentAnalysisModal.js
import React, { useState, useEffect } from "react";
import { Modal as MuiModal, Box, Button as MuiButton, Alert, CircularProgress, Tabs, Tab } from '@mui/material';
import axiosInstance from "../../../../utils/axiosInstance";

export default function CommentAnalysisModal({ isOpen, onClose, post, permission }) {
    const [analysis, setAnalysis] = useState(null);
    const [loading, setLoading] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);
    const [training, setTraining] = useState(false);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState(0);

    useEffect(() => {
        if (isOpen && post.is_analyzed) {
            fetchAnalysis();
        }
    }, [isOpen, post]);

    const fetchAnalysis = async () => {
        setLoading(true);
        setError('');
        
        try {
            const response = await axiosInstance.get(`/api/instagram/posts/${post.id}/analysis`);
            if (response.data.success) {
                setAnalysis(response.data.data.analysis);
            }
        } catch (err) {
            console.error('Error fetching analysis:', err);
            setError(err.response?.data?.message || 'Failed to fetch analysis');
        } finally {
            setLoading(false);
        }
    };

    const handleAnalyze = async () => {
        setAnalyzing(true);
        setError('');
        
        try {
            const response = await axiosInstance.post(`/api/instagram/posts/${post.id}/analyze`);
            if (response.data.success) {
                setAnalysis(response.data.data.analysis);
                setError('Comments analyzed successfully!');
            } else {
                setError(response.data.message || 'Analysis failed');
            }
        } catch (err) {
            console.error('Error analyzing comments:', err);
            setError(err.response?.data?.message || 'Failed to analyze comments');
        } finally {
            setAnalyzing(false);
        }
    };

    const handleTrain = async () => {
        setTraining(true);
        setError('');
        
        try {
            const response = await axiosInstance.post(`/api/instagram/posts/${post.id}/train`);
            if (response.data.success) {
                setError('Analysis trained successfully!');
                setTimeout(() => {
                    onClose();
                }, 2000);
            } else {
                setError(response.data.message || 'Training failed');
            }
        } catch (err) {
            console.error('Error training analysis:', err);
            setError(err.response?.data?.message || 'Failed to train analysis');
        } finally {
            setTraining(false);
        }
    };

    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '95%', sm: '90%', md: '80%' },
        maxWidth: '1000px',
        maxHeight: '90vh',
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 3,
        borderRadius: '8px',
        outline: 'none',
        overflowY: 'auto'
    };

    const renderSentimentBar = (sentiment) => {
        if (!sentiment) return null;
        
        const { positive_percentage = 0, negative_percentage = 0, neutral_percentage = 0 } = sentiment;
        
        return (
            <div style={{ marginTop: '10px' }}>
                <div style={{ display: 'flex', height: '30px', borderRadius: '5px', overflow: 'hidden' }}>
                    {positive_percentage > 0 && (
                        <div 
                            style={{ 
                                width: `${positive_percentage}%`, 
                                backgroundColor: '#28a745',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '0.85em'
                            }}
                        >
                            {positive_percentage > 10 && `${positive_percentage}%`}
                        </div>
                    )}
                    {neutral_percentage > 0 && (
                        <div 
                            style={{ 
                                width: `${neutral_percentage}%`, 
                                backgroundColor: '#ffc107',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '0.85em'
                            }}
                        >
                            {neutral_percentage > 10 && `${neutral_percentage}%`}
                        </div>
                    )}
                    {negative_percentage > 0 && (
                        <div 
                            style={{ 
                                width: `${negative_percentage}%`, 
                                backgroundColor: '#dc3545',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '0.85em'
                            }}
                        >
                            {negative_percentage > 10 && `${negative_percentage}%`}
                        </div>
                    )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '5px', fontSize: '0.85em' }}>
                    <span>🟢 Positive: {positive_percentage}%</span>
                    <span>🟡 Neutral: {neutral_percentage}%</span>
                    <span>🔴 Negative: {negative_percentage}%</span>
                </div>
            </div>
        );
    };

    return (
        <MuiModal open={isOpen} onClose={onClose} aria-labelledby="analysis-modal-title">
            <Box sx={modalStyle}>
                <h5 id="analysis-modal-title" style={{ marginTop: 0, marginBottom: '20px' }}>
                    <i className="fa fa-bar-chart me-2"></i>
                    Comment Analysis
                </h5>

                {/* Post Info */}
                <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', gap: '15px' }}>
                        {(post.thumbnail_url || post.media_url) && (
                            <img 
                                src={post.thumbnail_url || post.media_url} 
                                alt="Post" 
                                style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px' }}
                            />
                        )}
                        <div style={{ flex: 1 }}>
                            <p style={{ margin: '0 0 5px 0', fontWeight: '500' }}>
                                {post.caption ? (post.caption.length > 150 ? post.caption.substring(0, 150) + '...' : post.caption) : 'No caption'}
                            </p>
                            <div style={{ fontSize: '0.9em', color: '#666' }}>
                                <span><i className="fa fa-heart text-danger me-1"></i>{post.like_count || 0} likes</span>
                                <span className="ms-3"><i className="fa fa-comment text-primary me-1"></i>{post.comments_count || 0} comments</span>
                            </div>
                        </div>
                    </div>
                </div>

                {error && (
                    <Alert severity={error.toLowerCase().includes('success') ? 'success' : 'error'} sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {!analysis && !loading && !analyzing && (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <i className="fa fa-bar-chart fa-4x text-muted mb-3"></i>
                        <p>This post hasn't been analyzed yet.</p>
                        {permission.canModify && (
                            <MuiButton 
                                variant="contained" 
                                onClick={handleAnalyze}
                                disabled={post.comments_count === 0}
                            >
                                <i className="fa fa-play me-2"></i>
                                Analyze Comments
                            </MuiButton>
                        )}
                        {post.comments_count === 0 && (
                            <p className="text-muted mt-2">No comments available to analyze</p>
                        )}
                    </div>
                )}

                {(loading || analyzing) && (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <CircularProgress />
                        <p className="mt-3">{analyzing ? 'Analyzing comments with AI...' : 'Loading analysis...'}</p>
                        <small className="text-muted">This may take a minute...</small>
                    </div>
                )}

                {analysis && !loading && (
                    <div>
                        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 2 }}>
                            <Tab label="Summary" />
                            <Tab label="Top Comments" />
                            <Tab label="Sentiment" />
                            <Tab label="Tags" />
                            <Tab label="Training Content" />
                        </Tabs>

                        {/* Summary Tab */}
                        {activeTab === 0 && (
                            <div>
                                <h6>Comments Summary</h6>
                                <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '15px' }}>
                                    <p style={{ margin: 0 }}>{analysis.summary}</p>
                                </div>
                                <div style={{ fontSize: '0.9em', color: '#666' }}>
                                    <span><strong>Total Comments Analyzed:</strong> {analysis.total_comments_analyzed || 0}</span>
                                    <span className="ms-3"><strong>Tokens:</strong> {analysis.tokens || 0}</span>
                                </div>
                            </div>
                        )}

                        {/* Top Comments Tab */}
                        {activeTab === 1 && (
                            <div>
                                <h6>Top Comments</h6>
                                {analysis.top_comments && analysis.top_comments.length > 0 ? (
                                    <div>
                                        {analysis.top_comments.map((comment, index) => (
                                            <div key={index} style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '10px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                                    <strong>@{comment.username}</strong>
                                                    <span className="badge bg-primary">Relevance: {comment.relevance_score}/10</span>
                                                </div>
                                                <p style={{ margin: '5px 0' }}>{comment.text}</p>
                                                <div style={{ fontSize: '0.85em', color: '#666' }}>
                                                    <span><i className="fa fa-heart me-1"></i>{comment.like_count} likes</span>
                                                    <span className="ms-3">
                                                        Sentiment: 
                                                        <span className={`ms-1 ${
                                                            comment.sentiment === 'positive' ? 'text-success' : 
                                                            comment.sentiment === 'negative' ? 'text-danger' : 
                                                            'text-warning'
                                                        }`}>
                                                            {comment.sentiment}
                                                        </span>
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-muted">No top comments available</p>
                                )}
                            </div>
                        )}

                        {/* Sentiment Tab */}
                        {activeTab === 2 && (
                            <div>
                                <h6>Sentiment Analysis</h6>
                                {analysis.sentiment && (
                                    <div>
                                        <div style={{ marginBottom: '20px' }}>
                                            <strong>Overall Sentiment:</strong> 
                                            <span className={`ms-2 badge ${
                                                analysis.sentiment.overall === 'positive' ? 'bg-success' : 
                                                analysis.sentiment.overall === 'negative' ? 'bg-danger' : 
                                                analysis.sentiment.overall === 'mixed' ? 'bg-warning' :
                                                'bg-secondary'
                                            }`}>
                                                {analysis.sentiment.overall}
                                            </span>
                                        </div>

                                        {renderSentimentBar(analysis.sentiment)}

                                        <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                                            <div style={{ marginBottom: '10px' }}>
                                                <strong>Engagement Score:</strong> {analysis.sentiment.engagement_score}/10
                                            </div>
                                            {analysis.sentiment.key_emotions && analysis.sentiment.key_emotions.length > 0 && (
                                                <div>
                                                    <strong>Key Emotions:</strong>
                                                    <div style={{ marginTop: '5px' }}>
                                                        {analysis.sentiment.key_emotions.map((emotion, index) => (
                                                            <span key={index} className="badge bg-info me-1">{emotion}</span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Tags Tab */}
                        {activeTab === 3 && (
                            <div>
                                <h6>Classification Tags</h6>
                                {analysis.tags && analysis.tags.length > 0 ? (
                                    <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                                        {analysis.tags.map((tag, index) => (
                                            <span key={index} className="badge bg-secondary me-2 mb-2" style={{ fontSize: '1em', padding: '8px 12px' }}>
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-muted">No tags available</p>
                                )}
                            </div>
                        )}

                        {/* Training Content Tab */}
                        {activeTab === 4 && (
                            <div>
                                <h6>Merged Training Content</h6>
                                <div style={{ 
                                    padding: '15px', 
                                    backgroundColor: '#f8f9fa', 
                                    borderRadius: '8px',
                                    maxHeight: '400px',
                                    overflowY: 'auto',
                                    whiteSpace: 'pre-wrap',
                                    fontFamily: 'monospace',
                                    fontSize: '0.9em'
                                }}>
                                    {analysis.merged_content}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginTop: '20px' }}>
                    <MuiButton variant="outlined" onClick={onClose} disabled={analyzing || training}>
                        Close
                    </MuiButton>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        {permission.canModify && analysis && (
                            <MuiButton 
                                variant="outlined"
                                onClick={handleAnalyze}
                                disabled={analyzing || training}
                            >
                                <i className="fa fa-refresh me-2"></i>
                                Re-analyze
                            </MuiButton>
                        )}
                        {permission.canModify && analysis && !post.is_trained && (
                            <MuiButton 
                                variant="contained"
                                onClick={handleTrain}
                                disabled={analyzing || training}
                            >
                                {training ? (
                                    <><span className="spinner-border spinner-border-sm me-2"></span>Training...</>
                                ) : (
                                    <><i className="fa fa-graduation-cap me-2"></i>Train Chatbot</>
                                )}
                            </MuiButton>
                        )}
                        {post.is_trained && (
                            <span className="badge bg-success" style={{ padding: '10px', fontSize: '1em' }}>
                                <i className="fa fa-check me-1"></i>Already Trained
                            </span>
                        )}
                    </div>
                </Box>
            </Box>
        </MuiModal>
    );
}